/**
 * Local Storage management for resume data
 * Provides a single localStorage key to manage drafts and versions
 */

export const STORAGE_KEY = "MYSUME_STORAGE";

export interface VersionEntry {
  id: string;
  name: string;
  timestamp: number; // Unix timestamp
  content: string;
}

export interface StorageSchema {
  activeDraft: string | null;     // Current working text in the editor
  lastSaved: string | null;       // Snapshot from last "Save Changes" click
  versions: VersionEntry[];       // Array of named versions
}

const defaultStorage: StorageSchema = {
  activeDraft: null,
  lastSaved: null,
  versions: [],
};

/**
 * Get the entire storage object from localStorage
 */
export function getStorage(): StorageSchema {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultStorage };
    
    const parsed = JSON.parse(raw) as Partial<StorageSchema>;
    return {
      activeDraft: parsed.activeDraft ?? null,
      lastSaved: parsed.lastSaved ?? null,
      versions: parsed.versions ?? [],
    };
  } catch {
    console.warn("Failed to parse MYSUME_STORAGE, returning defaults");
    return { ...defaultStorage };
  }
}

/**
 * Save the entire storage object to localStorage
 */
export function setStorage(data: StorageSchema): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save to localStorage:", error);
  }
}

/**
 * Update specific fields in storage (partial update)
 */
export function updateStorage(updates: Partial<StorageSchema>): StorageSchema {
  const current = getStorage();
  const updated = { ...current, ...updates };
  setStorage(updated);
  return updated;
}

/**
 * Get the active draft content (returns null if none exists)
 */
export function getActiveDraft(): string | null {
  return getStorage().activeDraft;
}

/**
 * Update the active draft content (called on every edit, but we won't auto-save)
 * This is useful for persisting draft between page reloads
 */
export function setActiveDraft(content: string): void {
  updateStorage({ activeDraft: content });
}

/**
 * Get the last saved content (returns null if never saved)
 */
export function getLastSaved(): string | null {
  return getStorage().lastSaved;
}

/**
 * Save changes - updates both activeDraft and lastSaved
 */
export function saveChanges(content: string): StorageSchema {
  return updateStorage({
    activeDraft: content,
    lastSaved: content,
  });
}

/**
 * Get all saved versions
 */
export function getVersions(): VersionEntry[] {
  return getStorage().versions;
}

/**
 * Get a specific version by ID
 */
export function getVersionById(id: string): VersionEntry | undefined {
  return getStorage().versions.find((v) => v.id === id);
}

/**
 * Save a new named version
 * Returns the newly created version
 */
export function saveNewVersion(name: string, content: string): VersionEntry {
  const storage = getStorage();
  const newVersion: VersionEntry = {
    id: `v-${Date.now()}`,
    name,
    timestamp: Date.now(),
    content,
  };
  
  storage.versions.push(newVersion);
  setStorage(storage);
  
  return newVersion;
}

/**
 * Delete a version by ID
 */
export function deleteVersion(id: string): void {
  const storage = getStorage();
  storage.versions = storage.versions.filter((v) => v.id !== id);
  setStorage(storage);
}

/**
 * Check if lastSaved exists (for enabling/disabling the option)
 */
export function hasLastSaved(): boolean {
  return getStorage().lastSaved !== null;
}

/**
 * Clear all storage (for testing/reset purposes)
 */
export function clearStorage(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Generate a unique version name based on existing versions
 */
export function generateVersionName(): string {
  const versions = getVersions();
  const baseNum = versions.length + 1;
  return `Version ${baseNum}`;
}

